
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Crown } from 'lucide-react';

interface PrestigeDaysDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (days: number) => void;
  serverName: string;
  isLoading?: boolean;
}

export const PrestigeDaysDialog: React.FC<PrestigeDaysDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  serverName,
  isLoading = false
}) => {
  const [days, setDays] = useState(30);

  const handleConfirm = () => {
    if (days > 0) {
      onConfirm(days);
    }
  };

  const handleClose = () => {
    setDays(30); // Reset to default
    onClose();
  };

  // Empêcher la fermeture automatique du dialog
  const handleOpenChange = (open: boolean) => {
    // Ne ferme le dialog que si on clique explicitement sur fermer
    if (!open) {
      return; // Ignore les tentatives de fermeture automatique
    }
  };

  // Options rapides pour les durées communes
  const quickOptions = [7, 15, 30, 60, 90, 180, 365];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="bg-slate-800 border-slate-700 max-w-md z-[10001] pointer-events-auto dialog-content"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            Activer le Prestige
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Choisissez la durée d'activation du Prestige pour "{serverName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Options rapides */}
          <div>
            <Label className="text-white text-sm font-medium">Options rapides</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {quickOptions.map((option) => (
                <Button
                  key={option}
                  variant={days === option ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDays(option)}
                  className={`text-xs ${
                    days === option 
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300 border-slate-600'
                  }`}
                >
                  {option}j
                </Button>
              ))}
            </div>
          </div>

          {/* Input personnalisé */}
          <div>
            <Label htmlFor="custom-days" className="text-white text-sm font-medium">
              Durée personnalisée (jours)
            </Label>
            <Input
              id="custom-days"
              type="number"
              min="1"
              value={days}
              onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
              className="mt-2 bg-slate-700 border-slate-600 text-white"
              placeholder="Nombre de jours"
            />
            <p className="text-xs text-slate-400 mt-1">
              Minimum 1 jour
            </p>
          </div>

          {/* Récapitulatif */}
          <div className="bg-slate-700/50 p-3 rounded-lg">
            <p className="text-sm text-slate-300">
              <strong className="text-yellow-400">Récapitulatif :</strong>
            </p>
            <p className="text-sm text-slate-300 mt-1">
              • Serveur : {serverName}
            </p>
            <p className="text-sm text-slate-300">
              • Durée : {days} jour{days > 1 ? 's' : ''}
            </p>
            <p className="text-sm text-slate-300">
              • Expiration : {new Date(Date.now() + days * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="bg-slate-700 text-white hover:bg-slate-600"
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || days < 1}
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            {isLoading ? 'Activation...' : `Activer ${days} jour${days > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
